/**
 * useIdeas Hook
 * 
 * Fetches and manages ideas for an opportunity.
 * Provides CRUD operations, voting, and filtering.
 * Encapsulates all API calls per Rule 6.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import {
  IdeaWithDetails,
  IdeaCategory,
  IdeaStatus,
  IdeaPriority,
  IdeaFilters,
  IdeaSortBy,
  CreateIdeaRequest,
  UpdateIdeaRequest,
  VoteType,
} from '../shared/types/idea';

// =============================================================================
// TYPES
// =============================================================================

interface UseIdeasProps {
  opportunityId: number;
  initialFilters?: IdeaFilters;
}

interface UseIdeasReturn {
  // Data
  ideas: IdeaWithDetails[];
  total: number;
  
  // Loading states
  loading: boolean;
  creating: boolean;
  voting: Record<number, boolean>; // ideaId -> loading
  
  // Error
  error: string | null;
  
  // Filters
  filters: IdeaFilters;
  setCategory: (category: IdeaCategory | 'all') => void;
  setStatus: (status: IdeaStatus | 'all') => void;
  setPriority: (priority: IdeaPriority | 'all') => void;
  setSortBy: (sortBy: IdeaSortBy) => void;
  
  // Actions
  createIdea: (data: CreateIdeaRequest) => Promise<IdeaWithDetails | null>;
  updateIdea: (ideaId: number, data: UpdateIdeaRequest) => Promise<boolean>;
  deleteIdea: (ideaId: number) => Promise<boolean>;
  voteIdea: (ideaId: number, voteType: VoteType | null) => Promise<boolean>;
  refresh: () => Promise<void>;
  
  // Computed
  statusCounts: Record<IdeaStatus | 'all', number>;
  categoryCounts: Record<IdeaCategory | 'all', number>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useIdeas({ opportunityId, initialFilters }: UseIdeasProps): UseIdeasReturn {
  // State
  const [ideas, setIdeas] = useState<IdeaWithDetails[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [voting, setVoting] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [filters, setFilters] = useState<IdeaFilters>(initialFilters || {
    category: 'all',
    status: 'all',
    priority: 'all',
    sort_by: 'newest',
  });

  // Fetch ideas
  const fetchIdeas = useCallback(async () => {
    if (!opportunityId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await authApi.getIdeas(opportunityId, filters);
      
      if (response.success) {
        setIdeas(response.ideas);
        setTotal(response.total);
      } else {
        setError(response.message || 'Failed to load ideas');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load ideas';
      logger.error('Failed to fetch ideas', err, 'useIdeas');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [opportunityId, filters]);

  // Initial fetch and refetch on filter change
  useEffect(() => {
    fetchIdeas();
  }, [fetchIdeas]);

  // Filter setters
  const setCategory = useCallback((category: IdeaCategory | 'all') => {
    setFilters(prev => ({ ...prev, category }));
  }, []);

  const setStatus = useCallback((status: IdeaStatus | 'all') => {
    setFilters(prev => ({ ...prev, status }));
  }, []);

  const setPriority = useCallback((priority: IdeaPriority | 'all') => {
    setFilters(prev => ({ ...prev, priority }));
  }, []);

  const setSortBy = useCallback((sort_by: IdeaSortBy) => {
    setFilters(prev => ({ ...prev, sort_by }));
  }, []);

  // Create idea
  const createIdea = useCallback(async (data: CreateIdeaRequest): Promise<IdeaWithDetails | null> => {
    if (!opportunityId || opportunityId === 0) {
      logger.error('Cannot create idea: Invalid opportunityId', null, 'useIdeas', { opportunityId });
      setError('Invalid opportunity');
      return null;
    }
    
    setCreating(true);
    setError(null);
    
    try {
      const response = await authApi.createIdea(opportunityId, data);
      
      if (response.success && response.idea) {
        setIdeas(prev => [response.idea, ...prev]);
        setTotal(prev => prev + 1);
        return response.idea;
      } else {
        const errorMsg = response.message || 'Failed to create idea';
        logger.error('Create idea failed', null, 'useIdeas', { errorMsg });
        setError(errorMsg);
        return null;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create idea';
      logger.error('Create idea exception', err, 'useIdeas');
      setError(message);
      return null;
    } finally {
      setCreating(false);
    }
  }, [opportunityId]);

  // Update idea
  const updateIdea = useCallback(async (ideaId: number, data: UpdateIdeaRequest): Promise<boolean> => {
    try {
      const response = await authApi.updateIdea(ideaId, data);
      
      if (response.success && response.idea) {
        setIdeas(prev => prev.map(idea => idea.id === ideaId ? response.idea : idea));
        return true;
      } else {
        setError(response.message || 'Failed to update idea');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update idea';
      logger.error('Update idea failed', err, 'useIdeas');
      setError(message);
      return false;
    }
  }, []);

  // Delete idea
  const deleteIdea = useCallback(async (ideaId: number): Promise<boolean> => {
    try {
      const response = await authApi.deleteIdea(ideaId);
      
      if (response.success) {
        setIdeas(prev => prev.filter(idea => idea.id !== ideaId));
        setTotal(prev => prev - 1);
        return true;
      } else {
        setError(response.message || 'Failed to delete idea');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to delete idea';
      logger.error('Delete idea failed', err, 'useIdeas');
      setError(message);
      return false;
    }
  }, []);

  // Vote on idea
  const voteIdea = useCallback(async (ideaId: number, voteType: VoteType | null): Promise<boolean> => {
    setVoting(prev => ({ ...prev, [ideaId]: true }));
    
    try {
      const response = await authApi.voteIdea(ideaId, { vote_type: voteType });
      
      if (response.success) {
        setIdeas(prev => prev.map(idea => {
          if (idea.id === ideaId) {
            return { ...idea, upvotes: response.upvotes, downvotes: response.downvotes, score: response.score, user_vote: voteType };
          }
          return idea;
        }));
        return true;
      } else {
        setError(response.message || 'Failed to vote');
        return false;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to vote';
      logger.error('Vote failed', err, 'useIdeas');
      setError(message);
      return false;
    } finally {
      setVoting(prev => ({ ...prev, [ideaId]: false }));
    }
  }, []);

  // Compute counts for filters
  const statusCounts = useMemo(() => {
    const counts: Record<IdeaStatus | 'all', number> = {
      all: ideas.length,
      open: 0,
      under_review: 0,
      approved: 0,
      rejected: 0,
      implemented: 0,
      deferred: 0,
    };
    ideas.forEach(idea => {
      if (counts[idea.status] !== undefined) {
        counts[idea.status]++;
      }
    });
    return counts;
  }, [ideas]);

  const categoryCounts = useMemo(() => {
    const counts: Record<IdeaCategory | 'all', number> = {
      all: ideas.length,
      improvement: 0,
      concern: 0,
      question: 0,
      opportunity: 0,
      general: 0,
    };
    ideas.forEach(idea => {
      counts[idea.category]++;
    });
    return counts;
  }, [ideas]);

  return {
    ideas,
    total,
    loading,
    creating,
    voting,
    error,
    filters,
    setCategory,
    setStatus,
    setPriority,
    setSortBy,
    createIdea,
    updateIdea,
    deleteIdea,
    voteIdea,
    refresh: fetchIdeas,
    statusCounts,
    categoryCounts,
  };
}

